<script setup lang="ts">
/** components */
import ProgressBar from '~/components/portals/ProgressBar.vue'

/** props */
const props = withDefaults(
  defineProps<{
    items?: Array<any>
    withProgressBar?: boolean
    duration?: number
    itemClass?: string
    dotsClass?: string
    dotsContainerClass?: string
  }>(),
  {
    items: () => [],
    withProgressBar: false,
    duration: 10,
    itemClass: '',
    dotsClass: '',
    dotsContainerClass: '',
  },
)

/** refs */
const interval = ref<ReturnType<typeof setInterval> | null>(null)
const activeItem = ref(0)
const { $cloudinary } = useNuxtApp()

/** computed */
const showProgressBar = computed(() => {
  return props.items.length >= 2 && props.withProgressBar
})

/** methods */
const changeActiveItem = (index: number) => {
  activeItem.value = index
  localClearInterval()
  initInterval()
}

const localClearInterval = () => {
  if (!interval.value) return
  clearInterval(interval.value)
}

const initInterval = () => {
  if (!showProgressBar.value) return
  const durationInMs = props.duration * 1000
  interval.value = setInterval(() => {
    activeItem.value = (activeItem.value + 1) % props.items.length
  }, durationInMs)
}

const getCoverImage = (item: any) => {
  const cover = item?.backgroundImage || item?.images?.desktop
  if (cover) {
    const resizedCover = $cloudinary.resize(cover, 'hero-image')
    return `url(${resizedCover})`
  }
  return ''
}

/** lifecycle */
onMounted(() => {
  initInterval()
})
onBeforeUnmount(() => {
  localClearInterval()
})
</script>
<template>
  <div>
    <template v-for="(item, index) in items">
      <div
        v-if="index === activeItem"
        :key="item?.slug"
        class="relative flex h-full flex-col justify-between overflow-hidden rounded-lg"
      >
        <div
          class="relative flex flex-1 flex-col justify-between bg-black/60 p-6 text-white"
          :class="itemClass"
        >
          <div
            data-testid="carousel-cover"
            class="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat mix-blend-multiply filter"
            :style="{
              backgroundImage: getCoverImage(item),
            }"
          />
          <div class="z-10 h-full">
            <slot name="content" :item="item" />
          </div>
          <div
            class="z-10 flex items-end justify-between"
            :class="dotsContainerClass"
          >
            <slot name="action" :item="item" />
            <div
              data-testid="carousel-dots"
              class="flex gap-x-4"
              :class="dotsClass"
            >
              <button
                v-for="(_, i) in items"
                :key="i"
                :data-testid="`carousel-dot-${i}`"
                class="h-3 w-3 cursor-pointer rounded-full bg-white"
                :class="{ '!bg-p-500': i === activeItem }"
                @click="changeActiveItem(i)"
              />
            </div>
          </div>
        </div>

        <ProgressBar
          v-if="showProgressBar"
          :duration="`${duration}s`"
          class="z-10"
        />
      </div>
    </template>
  </div>
</template>
<style scoped>
.fade-in {
  animation: fade-in 0.5s ease-in-out;
}

@keyframes fade-in {
  from {
    opacity: 0.5;
  }

  to {
    opacity: 1;
  }
}
</style>
